import React, { useState, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Tooltip,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Backdrop,
  Button,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EditIcon from "@mui/icons-material/Edit";
import { AppConfig, roles, FINANCE_RESULT } from "../../../../../configs/admin-config";
import Avatar from "@mui/material/Avatar";
import {
  getAccessToken,
  getUserName,
  checkIfRoleExists,
} from "@utils/oauth";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  parentDiv: {
    marginBottom: 15,
  },
  questionParent: {
    display: "flex",
  },
  questionIndex: {
    flexBasis: "4%",
    "@media(max-width: 900px)": {
      flexBasis: "5%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "10%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "15%",
    },
  },
  questionDetails: {
    flexBasis: "96%",
    textAlign: "justify",
    "@media(max-width: 900px)": {
      flexBasis: "94%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "90%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "85%",
    },
  },
  commentCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: 4,
    padding: "12px",
    marginBottom: 16,
    border: "1px solid #e0e0e0",
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  userBadge: {
    backgroundColor:theme.palette.background.paper,
    color:theme.palette.primary.main,
    borderRadius: 20,
    padding: "12px 2px",
    fontWeight: 400,
    fontSize: "14px",
    border: `1px solid ${theme.palette.text.secondary}`,
  },
  editIconButton: {
    padding: 8,
  },
  commentText: {
    backgroundColor: "#f9f9f9",
    fontSize: "15px",
    paddingTop: "2px",
    paddingLeft: "2px",
    whiteSpace: "pre-wrap",
  },
  editingField: {
    backgroundColor: "#ffffff",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  actionButtons: {
    display: "flex",
    gap: 8,
    marginTop: 12,
  },
  cancelBtn: {
    backgroundColor: "transparent",
    color: "#000000",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "4px 14px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
  },
  newCommentField: {
    backgroundColor: "#f9f9f9",
    borderRadius: 12,
    padding: "16px",
    border: "1px solid #e0e0e0",
    marginBottom: 16,
  },
  saveCommentBtn: {
    backgroundColor: theme.palette.secondary.main,
    color: "#ffffff",
    borderRadius: 4,
    padding: "4px 14px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#ba5502",
    },
    "& .MuiButton-startIcon": {
      marginRight: 4,
    },
  },
  ghostPlaceholder: {
    backgroundColor: "#ffffff",
    borderRadius: 4,
    padding: "12px",
    marginBottom: 16,
    border: "1px solid #d0d0d0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#000000",
    },
  },
  ghostCommentText: {
    backgroundColor: "#ffffff",
    fontSize: "15px",
    fontWeight: "400",
    paddingTop: "2px",
    paddingBottom: "10px",
    color: "#b0b0b0",
    transition: "all 0.2s ease",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const CommentInputs = (props) => {
  const { classes } = useStyles();
  const [showNewCommentField, setShowNewCommentField] = useState(false);
  const [allComments, setAllComments] = useState(props.comments);
  const [backdropOpen, setbackDrop] = useState(false);
  const [successSnack, setSuccess] = useState(false);
  const [errorSnack, setError] = useState(false);
  const [currentComment, setCurrentComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedComment, setEditedComment] = useState("");

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSuccess(false);
    setError(false);
  };

  const addNewComment = () => {
    setShowNewCommentField(true);
    setCurrentComment("");
  };

  const cancelNewComment = () => {
    setShowNewCommentField(false);
    setCurrentComment("");
  };

  const saveNewComment = async () => {
    if (currentComment.trim() === "") return;

    setbackDrop(true);
    
    const newComment = {
      sortIndex: allComments.filter(c => c.questionId === props.questionId).length,
      userEmail: getUserName(),
      comment: currentComment,
      questionId: parseInt(props.questionId),
      companyId: parseInt(props.companyId),
    };

    let status = "";
    const HAS_Q42_COMMENTS = props.questionId === 42 || 
                            allComments.some(c => c.questionId === 42);
    const HAS_Q43_COMMENTS = props.questionId === 43 || 
                            allComments.some(c => c.questionId === 43);

    if (HAS_Q42_COMMENTS && !HAS_Q43_COMMENTS) {
      status = FINANCE_RESULT.REVIEWER_PENDING;
    } else if (HAS_Q42_COMMENTS && HAS_Q43_COMMENTS) {
      status = FINANCE_RESULT.APPROVAL_PENDING;
    }

    const financePayload = [{
      ...newComment,
      financeResult: status
    }];

    const requestURL = AppConfig.baseUrl + AppConfig.financeComments;

    try {
      let response = await fetch(requestURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify(financePayload),
      });
      let body = await response.json();
      if (response.status === 200) {
        setSuccess(true);
        setCurrentComment("");
        setShowNewCommentField(false);
        props.formRender();
      } else {
        console.error(body.message);
        setError(true);
      }
      setbackDrop(false);
    } catch (error) {
      console.error(error);
      setError(true);
      setbackDrop(false);
    }
  };

  const startEdit = (commentId, initialText) => {
    setEditingCommentId(commentId);
    setEditedComment(initialText);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditedComment("");
  };

  useEffect(() => {
    setAllComments(props.comments);
  }, [props.comments]);

  const saveEditedComment = async (commentId) => {
    setbackDrop(true);
    const requestURL =
      AppConfig.baseUrl + AppConfig.financeComments + commentId;
    const existingComment = allComments.find((c) => c.commentId === commentId);
    const sortIndex = existingComment ? existingComment.sortIndex : 0;
    try {
      let response = await fetch(requestURL, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
        body: JSON.stringify({
          sortIndex: sortIndex,
          userEmail: getUserName(),
          comment: editedComment,
          questionId: parseInt(props.questionId),
          companyId: parseInt(props.companyId),
        }),
      });
      let body = await response.json();
      if (response.status === 200) {
        setSuccess(true);
        setEditingCommentId(null);
        setEditedComment("");
        setAllComments((prevComments) =>
          prevComments.map((comment) =>
            comment.commentId === commentId
              ? { ...comment, comment: editedComment }
              : comment
          )
        );
        props.formRender();
      } else {
        console.error(body.message);
        setError(true);
      }
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setbackDrop(false);
    }
  };

  const questionComments = allComments.filter(
    (comment) => comment.questionId === props.questionId
  );

  const canAddComment =
    (props.questionId === 42 && checkIfRoleExists(roles.financeCreator)) ||
    (props.questionId === 43 && checkIfRoleExists(roles.financeReviewer));

  return (
    <div className={classes.parentDiv}>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Snackbar
        open={successSnack}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity="success">
          Success!
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={errorSnack} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="error">
          Failed!
        </Alert>
      </Snackbar>
      {/* Finance opinion*/}
      <div className={classes.questionParent}>
        <div className={classes.questionIndex}></div>
          <div className={classes.questionDetails}>
            {questionComments.map((item, key) => {
              const isEditing = editingCommentId === item.commentId;
              return (
                <div key={item.commentId} className={classes.commentCard}>
                  <div className={classes.commentHeader}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title={item.userEmail}>
                        <Chip
                          icon={<Avatar sx={{ width: 20, height: 20, fontSize: 13 }} />}
                          className={classes.userBadge}
                          label={item.userName}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      </Tooltip>
                    </div>
                    {!isEditing && (
                      <IconButton
                        size="small"
                        className={classes.editIconButton}
                        onClick={() => startEdit(item.commentId, item.comment)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>

                  {isEditing ? (
                    <>
                      <TextField
                        value={editedComment}
                        onChange={(e) => setEditedComment(e.target.value)}
                        variant="outlined"
                        multiline
                        minRows={3}
                        fullWidth
                        className={classes.editingField}
                      />
                      <div className={classes.actionButtons}>
                        <Button
                          className={classes.saveCommentBtn}
                          variant="contained"
                          size="small"
                          onClick={() => saveEditedComment(item.commentId)}
                        >
                          Save
                        </Button>
                        <Button
                          className={classes.cancelBtn}
                          variant="outlined"
                          size="small"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className={classes.commentText}>{item.comment}</div>
                  )}
                </div>
              );
            })}

            {((questionComments.length === 0 &&
              !showNewCommentField &&
              canAddComment) ||
              (canAddComment &&
                questionComments.length > 0 &&
                !showNewCommentField)) && (
              <div className={classes.ghostPlaceholder} onClick={addNewComment}>
                <div className={classes.ghostCommentText}>
                  Add comment...
                </div>
              </div>
            )}

            {showNewCommentField && (
              <div className={classes.newCommentField}>
                <TextField
                  placeholder="Add comment..."
                  value={currentComment}
                  onChange={(e) => setCurrentComment(e.target.value)}
                  variant="outlined"
                  multiline
                  minRows={3}
                  fullWidth
                  className={classes.editingField}
                />
                <div className={classes.actionButtons}>
                  <Button
                    className={classes.saveCommentBtn}
                    variant="contained"
                    size="small"
                    onClick={saveNewComment}
                    disabled={currentComment.trim() === ""}
                  >
                    Save
                  </Button>
                  <Button
                    className={classes.cancelBtn}
                    variant="outlined"
                    size="small"
                    onClick={cancelNewComment}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
      </div>
    </div>
  );
};

export default CommentInputs;
